/** Shared state hooks for the admin pages. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type ParamValue = string | number | null;

/**
 * URL-backed state helper. Returns a mutator that patches the query string
 * (omit/clear keys by passing `null` or empty string) and replaces history in
 * place — preserves refresh/deep-linking without polluting browser history.
 */
export function useUrlParams(): {
  params: URLSearchParams;
  update: (patch: Record<string, ParamValue>) => void;
} {
  const [params, setParams] = useSearchParams();
  const update = useCallback(
    (patch: Record<string, ParamValue>) => {
      const next = new URLSearchParams(params);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === '') next.delete(k);
        else next.set(k, String(v));
      }
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  return { params, update };
}

/**
 * Debounced search input. Keeps the typed value in local state for snappy
 * keystrokes, then fires `onCommit` after `delayMs` of inactivity.
 */
export function useDebouncedSearch(
  initial: string,
  delayMs: number,
  onCommit: (value: string) => void,
): { value: string; onChange: (v: string) => void } {
  const [value, setValue] = useState(initial);
  const timer = useRef<number>(0);
  // Keep the committed value in sync when the parent (e.g. URL) changes
  // independently of typing (back button, clear, etc.).
  useEffect(() => setValue(initial), [initial]);
  const onChange = useCallback(
    (v: string) => {
      setValue(v);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => onCommit(v), delayMs);
    },
    [delayMs, onCommit],
  );
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return { value, onChange };
}
